/**
 * Exports data to CSV using custom data transformation
 *
 * @param data - Array of objects to export
 * @param headers - Array of header labels
 * @param rowMapper - Function that transforms each data item into an array of values
 * @param filename - Name of the downloaded CSV file (without .csv extension)
 *
 * @example
 * * const employees = [
 *   { name: 'reed', value: 100 },
 *   { name: 'andrew', price: 27 }
 * ];
 *
 * exportToCsv(
 *   employees,
 *   ['Name', 'Value To Company'],
 *   (employee) => [employee.name, employee.age],
 *   'employee-export'
 * );
 */
export const exportToCsv = <T>(
  data: T[],
  headers: string[],
  rowMapper: (item: T) => any[],
  filename = 'export',
): void => {
  if (!data || data.length === 0) {
    return;
  }

  // transform data using custom mapper
  const dataArrays = data.map(item => rowMapper(item));

  // add headers
  dataArrays.unshift(headers);

  // wrap values in quotes and convert to CSV string
  const csvContent = dataArrays
    .map(row => row.map(value => `"${value === null ? '' : String(value)}"`).join(','))
    .join('\n');

  // create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
